/**
 * Wanneer de intro-tour start (18-10-2026, Jordy). De stappen en teksten
 * staan in lib/tour.ts; dit onderdeel beslist alleen het moment.
 *
 *  - Automatisch alleen voor chatters, één keer per deel per account
 *    (ui_prefs `tour.chats.done` / `tour.chat.done`, dus op elk apparaat).
 *    Deel A op Chats (Pro), deel B bij de eerste chat die hij daarna opent.
 *  - De Help-knop start hem opnieuw, voor iedereen (ook om te testen).
 *  - 19-10-2026 (Jordy): in één keer: na deel A opent de tour zelf een chat in
 *    tour-modus (`?tour=1`, niets als gelezen gemarkeerd) en loopt deel B door;
 *    daarna terug naar Chats. Zonder chat op de pagina: B bij de eerste chat
 *    die de chatter zelf opent.
 *  - Staat Chats in de Easy-stand, dan ontbreekt het startanker en wacht deel A
 *    (niet als gezien gemarkeerd) tot de chatter Pro opent.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMij } from '../lib/rechten'
import { useUiPref } from '../lib/inklappen'
import { STARTANKER, TOUR_GEZIEN, TOUR_START, eersteChatId, startTour, wachtOp, type TourDeel } from '../lib/tour'
import { useMobiel } from '../lib/mobiel'
import { HelpPaneel } from './HelpPaneel'

const VRAAG_B = 'tour.vraagB'
function leesVraagB(): boolean {
  try { return sessionStorage.getItem(VRAAG_B) === '1' } catch { return false }
}
function zetVraagB(aan: boolean) {
  try { if (aan) sessionStorage.setItem(VRAAG_B, '1'); else sessionStorage.removeItem(VRAAG_B) } catch { /* privé-venster */ }
}

export function IntroTour() {
  const mij = useMij()
  const chatter = String(mij.data?.role ?? '').toLowerCase() === 'chatter'
  const { pathname, search } = useLocation()
  const tourChat = new URLSearchParams(search).get('tour') === '1'
  const navigate = useNavigate()
  const [aGezien, zetAGezien, aGeladen] = useUiPref<boolean>(TOUR_GEZIEN.A, false)
  const [bGezien, zetBGezien, bGeladen] = useUiPref<boolean>(TOUR_GEZIEN.B, false)
  // Via de Help-knop gevraagd, los van wat er al gezien is. Deel B ook in
  // sessionStorage: het moet een verversing overleven tot de volgende chat.
  const gevraagd = useRef<Record<TourDeel, boolean>>({ A: false, B: leesVraagB() })
  const bezig = useRef(false)
  const [tik, setTik] = useState(0)
  // 19-10-2026 (Jordy, V5; vervangt "op de telefoon nooit vanzelf"): ook op de
  // telefoon (< 900 px) start de tour alleen vanzelf bij een echte eerste login,
  // met dezelfde vlag als op desktop (tour.chats.done / tour.chat.done in
  // ui_prefs, per account). De zoomstap valt daar weg (lib/tour, aanwezigeStappen).
  // Via de Help-knop blijft hij te openen. Desktop ongewijzigd.
  const mobiel = useMobiel()

  useEffect(() => {
    const start = () => {
      gevraagd.current = { A: true, B: true }
      zetVraagB(true)
      if (pathname !== '/conversations') navigate('/conversations')
      setTik((t) => t + 1)
    }
    window.addEventListener(TOUR_START, start)
    return () => window.removeEventListener(TOUR_START, start)
  }, [pathname, navigate])

  useEffect(() => {
    if (bezig.current) return
    const deel: TourDeel | null = pathname === '/conversations' ? 'A'
      : /^\/conversations\/show\//.test(pathname) ? 'B' : null
    if (!deel) return
    const gezien = deel === 'A' ? aGezien : bGezien
    const geladen = deel === 'A' ? aGeladen : bGeladen
    if (!gevraagd.current[deel] && !(chatter && geladen && !gezien)) return
    let weg = false
    void wachtOp(STARTANKER[deel]).then(async (er) => {
      if (weg || !er || bezig.current) return
      bezig.current = true
      // 19-10-2026 (Jordy): in één keer. Is er een chat om te openen, dan
      // eindigt A met "Open a chat" en opent de tour hem zelf in tour-modus
      // (`?tour=1`: niets wordt als gelezen gemarkeerd), waarna B doorloopt.
      const gestart = await startTour(deel, (voltooid) => {
        // Pas nu zoeken: bij de start kon de wachtrij nog aan het laden zijn.
        const chatId = deel === 'A' && voltooid ? eersteChatId() : null
        bezig.current = false
        gevraagd.current[deel] = false
        if (deel === 'B') zetVraagB(false)
        if (deel === 'A') { if (!aGezien) zetAGezien(true) } else if (!bGezien) zetBGezien(true)
        if (deel === 'A' && voltooid && chatId) {
          gevraagd.current.B = true
          zetVraagB(true)
          navigate(`/conversations/show/${chatId}?tour=1`)
        } else if (deel === 'B' && tourChat) {
          // Niet achterblijven in een chat die de tour koos.
          navigate('/conversations', { replace: true })
        }
      }, deel === 'A' ? { laatsteKnop: () => (eersteChatId() ? 'Open a chat →' : 'Got it') } : {})
      if (!gestart) bezig.current = false
    })
    return () => { weg = true }
    // zetAGezien/zetBGezien zijn bij elke render nieuw; bewust niet in de lijst.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, tourChat, chatter, aGezien, bGezien, aGeladen, bGeladen, tik, mobiel])

  return null
}

/** De Help-knop in de topbalk: opent het Help-paneel (tour, handleiding en
 *  de Help-assistent; components/HelpPaneel). */
export function HelpKnop() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="iconbtn" data-tour="help" aria-expanded={open}
              title="Help: tour, guide and the Help assistant"
              onClick={() => setOpen((v) => !v)}>
        ?
      </button>
      {open ? <HelpPaneel onClose={() => setOpen(false)} /> : null}
    </>
  )
}
